import React, { useState, useEffect, useRef, useContext } from "react";

import { DataGrid } from "@material-ui/data-grid";
import {
  Container,
  Grid,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import {
  DescriptionOutlined,
  Check,
  Close,
  FiberManualRecord,
} from "@material-ui/icons";

import { GridStyled } from "../../styles/GlobalStyle";
import MaskService from "../../utils/mask/maskService";
import { TitleTable } from "../../components/basic/TitleTable";
import { ContractDetail } from "./ContractDetail";
import { SyncedIcon, StatusIcon } from "./styles/Contracts";
import ModalLoading from "../../components/modalLoading/ModalLoading";
import {
  ContractsContext,
  ContractsContextProvider,
} from "./context/ContractsContext";
import useContractsPreviewController from "./controllers/ContractsPreviewController";
import Btn from "../../components/buttons/Btn";

function Page() {
  const [rowEdit, setRowEdit] = useState({});
  const [selectedContracts, setSelectedContracts] = useState([]);
  const searchRef = useRef(null);
  const statusRef = useRef("");

  const {
    page,
    setPage,
    countAllRows,
    allRows,
    loading,
    searchFilter,
    setSearchFilter,
    mainLoading,
    status,
    setStatus,
  } = useContext(ContractsContext);

  const {
    handleGetContracts,
    syncContractByAdmContractId,
    defineStatusDataByAcronym,
  } = useContractsPreviewController();

  const columns = [
    {
      field: "Acoes",
      headerName: "Acões",
      width: 80,
      renderCell: ({ row }) => (
        <Tooltip title="Detalhar Contrato">
          <IconButton onClick={() => setRowEdit(row)}>
            <DescriptionOutlined />
          </IconButton>
        </Tooltip>
      ),
      align: "center",
    },
    {
      field: "synced",
      headerName: "Sincronização",
      width: 80,
      renderCell: ({ row }) => (
        <>
          <SyncedIcon>
            {row.synced ? (
              <Check className={"synced"} />
            ) : (
              <Tooltip title="Sincronizar contrato">
                <IconButton
                  onClick={() => syncContractByAdmContractId([row.Id])}
                >
                  <Close className={"notSynced"} />
                </IconButton>
              </Tooltip>
            )}
          </SyncedIcon>
        </>
      ),
      align: "center",
      sortType: "basic",
    },
    {
      field: "ClienteCnpj",
      headerName: "CNPJ/CPF",
      width: 150,
      renderCell: ({ row }) =>
        MaskService.toMask(
          row?.ClienteCnpj.length > 11 ? "cnpj" : "cpf",
          row?.ClienteCnpj || ""
        ),
    },
    {
      field: "Razao",
      headerName: "Razão",
      minWidth: 240,
      flex: 1,
      renderCell: ({ row }) => row?.Empresa?.Razao || "",
    },
    {
      field: "Fantasia",
      headerName: "Fantasia",
      minWidth: 240,
      flex: 1,
      renderCell: ({ row }) => row?.Empresa?.Fantasia || "",
    },
    {
      field: "Tipo",
      headerName: "Tipo",
      minWidth: 60,
      flex: 0.5,
    },
    {
      field: "Data",
      headerName: "Data",
      minWidth: 90,
      flex: 0.5,
      renderCell: ({ row }) =>
        (row?.Data || "A").split("-").reverse().join("/"),
    },
    {
      field: "Status",
      headerName: "Status",
      minWidth: 70,
      align: "center",
      renderCell: ({ row }) => {
        const statusData = defineStatusDataByAcronym(row.Status);

        return (
          <>
            <StatusIcon>
              <Tooltip title={statusData.description}>
                <FiberManualRecord className={`${statusData.status}Contract`} />
              </Tooltip>
            </StatusIcon>
          </>
        );
      },
    },
  ];

  useEffect(() => {
    (async () => {
      handleGetContracts();
    })();
  }, [handleGetContracts, page, searchFilter, status]);

  const handleChangeFilter = (e) => {
    e.preventDefault();

    const textInInput = searchRef?.current?.value;
    const statusInput = statusRef?.current?.value;

    const isTextFilterEqual = searchFilter === textInInput;
    const isStatusFileterEqual = statusInput === status;

    if (isTextFilterEqual && isStatusFileterEqual) return;

    setSearchFilter(textInInput);
    setStatus(statusInput);
    setPage(0);
  };

  return (
    <>
      {mainLoading && <ModalLoading loading={mainLoading} />}
      <Container maxWidth="lg">
        {!!rowEdit?.Id && (
          <ContractDetail row={rowEdit} handleClose={() => setRowEdit({})} />
        )}
        <GridStyled
          container
          padding="20px 0 20px 0"
          justify="space-between"
          spacing={3}
        >
          <Grid item xs={12}>
            <TitleTable
              inputRef={searchRef}
              labelFilter="Filtro (CNPJ, Razão, Fantasia, Tipo)"
              title="Contratos"
              handleFilter={handleChangeFilter}
              loading={loading}
              additionalFilter={
                <FormControl variant="filled" style={{ minWidth: "10%" }}>
                  <InputLabel
                    shrink
                    id="demo-simple-select-placeholder-label-label"
                  >
                    Status
                  </InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    inputRef={statusRef}
                    defaultValue={""}
                    displayEmpty
                  >
                    <MenuItem value={""}>Todos</MenuItem>
                    <MenuItem value={"A"}>Ativos</MenuItem>
                    <MenuItem value={"C"}>Cancelados</MenuItem>
                    <MenuItem value={"S"}>Suspensos</MenuItem>
                  </Select>
                </FormControl>
              }
            />
          </Grid>

          <Grid item xs={12}>
            <Grid container style={{ justifyContent: "flex-end" }}>
              <Grid item>
                <Btn
                  disabled={!selectedContracts.length}
                  startIcon={<Check />}
                  label={`Sincronizar contratos (${selectedContracts.length})`}
                  type="submit"
                  onClick={() =>
                    syncContractByAdmContractId(
                      selectedContracts,
                      setSelectedContracts
                    )
                  }
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <DataGrid
              autoHeight
              disableColumnMenu
              loading={loading}
              rows={loading ? [] : allRows}
              rowCount={countAllRows}
              columns={columns}
              pageSize={10}
              getRowId={(row) => row.Id}
              page={page}
              onPageChange={(e) => setPage(e?.page || 0)}
              pagination
              paginationMode="server"
              disableColumnSelector={true}
              disableSelectionOnClick={true}
              disableDensitySelector={true}
              disableColumnReorder={true}
              checkboxSelection
              isRowSelectable={(params) => !params.row?.synced}
              onSelectionModelChange={(newSelection) => {
                setSelectedContracts(newSelection.selectionModel);
              }}
            />
          </Grid>
        </GridStyled>
      </Container>
    </>
  );
}

function ContractsPreview() {
  return (
    <ContractsContextProvider>
      <Page />
    </ContractsContextProvider>
  );
}

export default ContractsPreview;
